<template>
  <div id="app">
    <router-view v-if="isPublicPage" />
    <template v-else>
      <div class="header-layout">
        <div class="common-layout">
          <el-col :span="2">
            <div class="system-name">灵柚管理系统</div>
          </el-col>
          <el-col :span="16" style="padding: 0px; min-height: 1px; min-width: 800px; flex: 1">
            <el-menu class="menu-item" mode="horizontal" :default-active="activeTopPath" @select="handleTopSelect">
              <el-menu-item v-for="item in topMenus" :key="item.path" :index="item.path">
                <template slot="title">
                  <div class="menu-item-comment">
                    <i class="menu-icon" :class="item.icon || 'el-icon-menu'"></i>
                    <div class="comment">{{ item.name }}</div>
                  </div>
                </template>
              </el-menu-item>
            </el-menu>
          </el-col>
          <el-col :span="3">
            <div class="shop-switch-box">
              <el-select v-model="shopType" size="mini" placeholder="全部店铺" @change="onShopTypeChange">
                <el-option label="全部店铺" value=""></el-option>
                <el-option label="灵柚" value="1"></el-option>
                <el-option label="萧发" value="2"></el-option>
                <el-option label="誉腾" value="3"></el-option>
                <el-option label="甄昂" value="4"></el-option>
                <el-option label="众联" value="8"></el-option>
              </el-select>
            </div>
          </el-col>
          <el-col :span="3">
            <div class="user-account-container">
              <el-menu mode="horizontal" name="userMenu" background-color="#3064c7" text-color="#fff">
                <el-submenu index="1">
                  <template slot="title">当前账户：{{ currentUserName }}</template>
                  <el-menu-item index="1-1" @click="openPasswordDialog">
                    <span class="tab-name">修改密码</span>
                  </el-menu-item>
                  <el-menu-item index="1-2" @click="quit">
                    <span class="tab-name">安全退出</span>
                  </el-menu-item>
                </el-submenu>
              </el-menu>
            </div>
          </el-col>
        </div>
      </div>
      <div class="main-content-layout">
        <el-row class="left-nav">
          <el-col :span="24">
            <el-menu :default-active="activeLeftPath" @select="handleLeftSelect" text-color="#d1e1ff" active-text-color="#8d8d8d">
              <el-menu-item v-for="item in leftMenus" :key="item.path" :index="item.path">
                <div class="nav-title">{{ item.name }}</div>
              </el-menu-item>
            </el-menu>
          </el-col>
        </el-row>
        <div class="ds-center">
          <keep-alive>
            <router-view />
          </keep-alive>
        </div>
      </div>
      <el-dialog
        title="修改密码"
        :visible.sync="passwordDialogVisible"
        width="440px"
        :close-on-click-modal="false"
        @closed="resetPasswordForm"
      >
        <el-form
          ref="passwordForm"
          :model="passwordForm"
          :rules="passwordRules"
          label-width="100px"
          size="small"
          @submit.native.prevent
        >
          <el-form-item label="旧密码" prop="oldPassword">
            <el-input
              v-model="passwordForm.oldPassword"
              type="password"
              autocomplete="current-password"
              show-password
              @keyup.enter.native="submitPasswordChange"
            />
          </el-form-item>
          <el-form-item label="新密码" prop="newPassword">
            <el-input
              v-model="passwordForm.newPassword"
              type="password"
              autocomplete="new-password"
              show-password
              @keyup.enter.native="submitPasswordChange"
            />
          </el-form-item>
          <el-form-item label="确认新密码" prop="confirmPassword">
            <el-input
              v-model="passwordForm.confirmPassword"
              type="password"
              autocomplete="new-password"
              show-password
              @keyup.enter.native="submitPasswordChange"
            />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button size="small" :disabled="passwordSubmitting" @click="passwordDialogVisible = false">取消</el-button>
          <el-button size="small" type="primary" :loading="passwordSubmitting" @click="submitPasswordChange">确认修改</el-button>
        </div>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { loginModules } from "@r";
import { clearAuthToken } from "@/utils/auth";

// 顶栏用后端 sys_menu 的顶级 DIR 节点渲染;
// 每个 DIR 下的 MENU 节点作为左栏。
// BUTTON 节点只提供 perms,不出现在菜单里。
export default {
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请再次输入新密码"));
      } else if (value !== this.passwordForm.newPassword) {
        callback(new Error("两次输入的新密码不一致"));
      } else {
        callback();
      }
    };
    return {
      shopType: localStorage.getItem("shopType"),
      passwordDialogVisible: false,
      passwordSubmitting: false,
      passwordForm: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      passwordRules: {
        oldPassword: [{ required: true, message: "请输入旧密码", trigger: "blur" }],
        newPassword: [{ required: true, message: "请输入新密码", trigger: "blur" }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: ["blur", "change"] }],
      },
    };
  },
  computed: {
    ...mapState({
      menuTree: state => state.user.menuTree,
      profile: state => state.user.profile,
    }),
    isPublicPage() {
      // 登录页、403、404 都由 route meta.public 声明,布局不套壳
      return !!(this.$route.meta && this.$route.meta.public);
    },
    currentUserName() {
      return (this.profile && (this.profile.nickname || this.profile.username)) || "";
    },
    // 顶栏:后端菜单树的一级节点 (menuType=DIR 或者没有父的 MENU)
    topMenus() {
      return (this.menuTree || []).map(node => ({
        // DIR 本身只是菜单分组，没有页面组件；点击时应进入第一个子页面。
        path: this.firstChildPath(node) || node.path || "",
        name: node.name,
        icon: node.icon,
        raw: node,
      })).filter(m => m.path);
    },
    // 当前顶栏选中项:根据当前 route path 反查在哪个顶级下
    activeTopPath() {
      const cur = this.$route.path;
      for (const top of this.topMenus) {
        if (this.pathBelongs(top.raw, cur)) return top.path;
      }
      return "";
    },
    // 左栏:当前顶级节点下的直接 MENU 子节点
    leftMenus() {
      const cur = this.$route.path;
      for (const top of this.menuTree || []) {
        if (this.pathBelongs(top, cur)) {
          return (top.children || [])
            .filter(c => c.menuType !== "BUTTON" && c.path)
            .map(c => ({ path: c.path, name: c.name }));
        }
      }
      return [];
    },
    activeLeftPath() {
      return this.$route.path;
    },
  },
  methods: {
    firstChildPath(node) {
      if (!node.children) return "";
      for (const c of node.children) {
        if (c.menuType === "MENU" && c.path && c.component) return c.path;
        const descendantPath = this.firstChildPath(c);
        if (descendantPath) return descendantPath;
      }
      return "";
    },
    pathBelongs(topNode, path) {
      // path 命中该顶级节点自身或它任一子孙(不含 BUTTON)
      const check = (n) => {
        if (n.path && n.menuType !== "BUTTON" && (n.path === path || path.indexOf(n.path + "/") === 0)) return true;
        if (n.children) {
          for (const c of n.children) {
            if (check(c)) return true;
          }
        }
        return false;
      };
      return check(topNode);
    },
    handleTopSelect(path) {
      if (path && path !== this.$route.path) this.$router.push(path);
    },
    handleLeftSelect(path) {
      if (path && path !== this.$route.path) this.$router.push(path);
    },
    onShopTypeChange(shopType) {
      localStorage.setItem("shopType", shopType);
      location.reload();
    },
    openPasswordDialog() {
      this.resetPasswordForm();
      this.passwordDialogVisible = true;
      this.$nextTick(() => {
        const form = this.$refs.passwordForm;
        if (form) form.clearValidate();
      });
    },
    resetPasswordForm() {
      this.passwordForm = {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.passwordSubmitting = false;
      this.$nextTick(() => {
        const form = this.$refs.passwordForm;
        if (form) form.clearValidate();
      });
    },
    submitPasswordChange() {
      if (this.passwordSubmitting) return;
      const form = this.$refs.passwordForm;
      if (!form) return;
      form.validate(valid => {
        if (!valid) return;
        this.passwordSubmitting = true;
        loginModules.changePassword({ ...this.passwordForm })
          .then(() => {
            this.passwordDialogVisible = false;
            this.$message.success("密码修改成功，请使用新密码重新登录");
            return this.$store.dispatch("user/logout").catch(() => {});
          })
          .then(() => this.$router.replace("/client/loginTest"))
          .catch(e => {
            this.$message.error((e && e.message) || "密码修改失败");
          })
          .finally(() => {
            this.passwordSubmitting = false;
          });
      });
    },
    quit() {
      this.$store.dispatch("user/logout").finally(() => {
        this.$message.success("登出成功");
        this.$router.replace("/client/loginTest");
      }).catch(() => {
        clearAuthToken();
        this.$router.replace("/client/loginTest");
      });
    },
  },
};
</script>

<style lang="scss">
@import "./style/common";

#app {
  margin: 0;
  padding: 0;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;

  .header-layout {
    width: 100%;
    height: 64px;
    background-color: #3064c7;
    min-width: 1280px;

    .common-layout {
      margin: 0 auto;
      height: 100%;
      padding: 0 20px;

      .system-name {
        font-size: 18px;
        color: white;
        display: inline-block;
        vertical-align: top;
        margin-top: 16px;
        min-width: 120px;
      }

      .menu-item {
        max-height: 64px;
        overflow: hidden;
        transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
        &:hover {
          max-height: 100vh;
          transition: max-height 1s ease-in-out;
        }
        .el-menu-item { padding: 0; }
        .menu-item-comment {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 64px;
          .menu-icon {
            font-size: 22px;
            color: #ffffff;
            line-height: 1;
          }
          .comment {
            width: 100%;
            font-size: 12px;
            line-height: 17px;
            color: #ffffff;
            text-align: center;
            opacity: 0.9;
          }
        }
      }
      .el-row { margin: 0; }
      .el-menu-item, .el-submenu__title {
        height: 64px;
        border: unset;
      }
      .el-menu {
        background-color: #3064c7;
        .el-menu-item.is-active {
          height: 64px;
          border-bottom: unset;
          color: #fff;
          background-color: #2958b1;
          .comment { font-weight: 500; opacity: 1; }
        }
      }
      .el-menu.el-menu--horizontal { border: none; }
      .el-menu--horizontal .el-menu-item {
        color: #fff;
        font-size: 16px;
        width: 88px;
      }
      .el-menu--horizontal .el-menu-item:not(.is-disabled):hover { background-color: #3064c7; }
      .shop-switch-box { float: right; margin-top: 17px; width: 100px; }
      .user-account-container {
        font-size: 16px;
        color: white;
        display: inline-block;
        vertical-align: top;
        float: right;
        min-width: 100px;
        .el-submenu__title { height: 50px; }
        .el-menu--horizontal { border-bottom: none; }
      }
    }
  }

  .main-content-layout {
    width: 100%;
    margin: 0 auto;
    min-width: 1280px;

    .left-nav {
      display: inline-block;
      width: 160px;
      vertical-align: top;
      height: 100%;
      float: left;

      .el-menu-item {
        height: 40px;
        line-height: 40px;
        width: 100%;
        border-right: 3px solid white;
        text-align: left;
        font-size: 16px;
        color: #d1e1ff;
        padding-left: 20px;
        span, .nav-title {
          width: 150px;
          height: 40px;
          font-family: PingFang SC;
          line-height: 40px;
          color: #333;
          font-size: 14px;
        }
        &.active, &:hover {
          background-color: rgba(48, 100, 199, 0.1);
          border-right: 3px solid #3064c7;
          .nav-title { color: #3064c7; }
        }
      }
      .is-active {
        background-color: rgba(48, 100, 199, 0.1);
        border-right: 3px solid #3064c7;
        .nav-title, span { color: #3064c7; }
      }
    }

    .ds-center {
      width: calc(100% - 170px);
      min-height: 670px;
      display: inline-block;
      vertical-align: top;
      background-color: #f0f0f0;
      padding-left: 10px;

      .header-box {
        background-color: white;
        margin-right: 10px;
      }
    }
  }
}
</style>
